import moment from "moment/moment";
import React from "react";
import { 
  FiEdit2, 
  FiTrash2, 
  FiFilter, 
  FiPlus, 
  FiTag, 
  FiDollarSign, 
  FiCalendar,
  FiClock,
  FiGrid,
  FiType,
  FiHash,
  FiRefreshCw,
  FiSearch,
  FiList
} from "react-icons/fi";
import { 
  MdOutlineEdit, 
  MdOutlineDelete, 
  MdOutlineAdd,
  MdOutlineFilter,
  MdOutlineCategory,
  MdOutlineAttachMoney,
  MdOutlineCalendarToday,
  MdOutlineAccessTime
} from "react-icons/md";
import { LiaEdit } from "react-icons/lia";
import { RiDeleteBin6Line } from "react-icons/ri";
import { CiFilter } from "react-icons/ci";
import SortableHeader from "../../../components/SortableHeader";

const Plantable = ({
  TableName,
  TableHeader,
  TableData,
  filter,
  setFilter,
  handleSearch,
  handleDelete,
  editAction,
  handleFilter,
  currentSortKey,
  currentSortOrder,
  onSort
}) => {
  // Status badge for plan type
  const TypeBadge = ({ type }) => {
    const typeConfig = {
      'boost': { 
        icon: FiTag, 
        color: 'text-blue-700 bg-blue-50 border-blue-200', 
        label: 'Boost' 
      },
      'banner': { 
        icon: FiGrid, 
        color: 'text-purple-700 bg-purple-50 border-purple-200', 
        label: 'Banner' 
      },
      'advertisement': { 
        icon: FiList, 
        color: 'text-emerald-700 bg-emerald-50 border-emerald-200', 
        label: 'Advertisement' 
      }
    };
    const config = typeConfig[type?.toLowerCase()] || typeConfig['boost'];
    const Icon = config.icon;
    
    return (
      <span className={`
        inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border
        ${config.color}
      `}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  return (
    <div className="w-full">
      <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100/80 overflow-hidden">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 md:p-5 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
          <div className="flex items-center gap-3">
            <div className="p-1.5 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-lg">
              <FiTag className="w-4 h-4 text-[#715EFE]" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-800">{TableName}</h2>
              <p className="text-xs text-gray-400 hidden sm:block">
                {TableData?.length || 0} plans configured
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {/* Filter */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FiFilter className="text-gray-400 w-4 h-4" />
              </div>
              <select
                className="appearance-none w-full h-10 pl-9 pr-8 py-2 bg-white border border-gray-200 text-sm text-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
                value={filter.type}
                onChange={(e) => handleFilter("type", e.target.value)}
              >
                <option value="">All Types</option>
                <option value="boost">Boost</option>
                <option value="banner">Banner</option>
                <option value="advertisement">Advertisement</option>
              </select>
              <FiClock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Create Button */}
            <button
              className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#855DF6] to-[#715EFE] rounded-lg hover:shadow-lg hover:shadow-[#715EFE]/25 transition-all duration-200 whitespace-nowrap"
              onClick={() => editAction(null)}
            >
              <FiPlus className="w-4 h-4" />
              <span>Create New Plan</span>
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50/80 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                {TableHeader?.map((heading, index) => {
                  const headerIcons = {
                    '#': null,
                    'Title': FiTag,
                    'Price': FiDollarSign,
                    'Duration': FiClock,
                    'Type': FiType,
                    'CreatedAt': FiCalendar,
                    'Action': null
                  };
                  const Icon = headerIcons[heading.label] || headerIcons[heading.label?.trim()];
                  return (
                    <td key={index} className="px-3 md:px-4 py-3 whitespace-nowrap">
                      <SortableHeader
                        label={heading.label}
                        sortKey={heading.sortKey}
                        currentSortKey={currentSortKey}
                        currentSortOrder={currentSortOrder}
                        onSort={onSort}
                        icon={Icon}
                      />
                    </td>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {TableData && TableData.length > 0 ? (
                TableData?.map((learner, index) => (
                  <tr key={index} className="hover:bg-gray-50/70 transition-colors duration-150 group">
                    <td className="px-3 md:px-4 py-3">
                      <span className="text-sm font-medium text-gray-400">
                        {index + 1}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 flex items-center justify-center">
                          <FiTag className="w-3.5 h-3.5 text-[#715EFE]" />
                        </div>
                        <span className="text-sm font-medium text-gray-800 truncate max-w-[150px]">
                          {learner.title || '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <span className="text-sm font-semibold text-emerald-600">
                        ${learner.price || 0}
                      </span>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <FiClock className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-sm text-gray-700">
                          {learner?.durationInDays || 0} Day{learner?.durationInDays !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <TypeBadge type={learner?.type} />
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-700 whitespace-nowrap">
                          {learner?.createdAt ? moment(learner.createdAt).format("MMM D, YYYY") : '-'}
                        </span>
                        {learner?.createdAt && (
                          <span className="text-xs text-gray-400">
                            {moment(learner.createdAt).format("h:mm A")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          className="p-1.5 text-[#715EFE] hover:text-[#855DF6] hover:bg-[#715EFE]/10 rounded-lg transition-all duration-200"
                          onClick={() => editAction(learner)}
                          title="Edit Plan"
                        >
                          <FiEdit2 className="w-4 h-4" />
                        </button>
                        <button
                          className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all duration-200"
                          onClick={() => handleDelete(learner._id)}
                          title="Delete Plan"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={TableHeader?.length || 7} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="p-4 bg-gray-50 rounded-full">
                        <FiTag className="w-10 h-10 text-gray-300" />
                      </div>
                      <p className="text-sm font-medium text-gray-500">No plans found</p>
                      <p className="text-xs text-gray-400">Create your first plan</p>
                      <button
                        onClick={() => editAction(null)}
                        className="mt-2 flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#715EFE] border border-[#715EFE] rounded-lg hover:bg-[#715EFE] hover:text-white transition-all duration-200"
                      >
                        <FiPlus className="w-4 h-4" />
                        Create New Plan
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {TableData && TableData.length > 0 && (
          <div className="px-4 py-3 bg-gray-50/50 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Showing {TableData.length} plan{TableData.length !== 1 ? 's' : ''}
            </span>
            <span className="text-xs text-gray-400">
              Last updated: {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Plantable;